import { useEffect } from "react";
import { PageHeader } from "../components/lendx/PageHeader";
import { Reveal } from "../components/lendx/Reveal";
import { DISCLAIMER } from "../lib/data";
import { applySEO } from "../lib/scroll";

const Section = ({ title, children, testId }) => (
  <Reveal className="border-b border-gray-200 py-10">
    <h2 className="font-heading text-xl font-bold tracking-tight text-navy md:text-2xl" data-testid={testId}>{title}</h2>
    <div className="mt-4 space-y-3 text-sm leading-relaxed text-gray-600 md:text-base">{children}</div>
  </Reveal>
);

const LegalLayout = ({ overline, title, sub, testId, seoTitle, children }) => {
  useEffect(() => {
    applySEO(seoTitle, sub);
  }, [seoTitle, sub]);
  return (
    <main data-testid={`${testId}-page`}>
      <PageHeader overline={overline} title={title} sub={sub} testId={testId} />
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-3xl px-5 md:px-8">{children}</div>
      </section>
    </main>
  );
};

export function PrivacyPage() {
  return (
    <LegalLayout
      overline="Legal"
      title="Privacy Policy"
      sub="How LendX collects, uses, and protects your information."
      testId="privacy"
      seoTitle="Privacy Policy | LendX"
    >
      <Section title="Information We Collect" testId="privacy-collect">
        <p>When you submit an enquiry, we collect your name, contact details, city, requirement type, requested loan amount, and any property or business details you choose to share.</p>
      </Section>
      <Section title="How We Use Your Information" testId="privacy-use">
        <p>Your information is used solely to assess your financing requirement, contact you regarding suitable financing options, and coordinate with financial institutions where you authorize us to do so.</p>
      </Section>
      <Section title="Sharing of Information" testId="privacy-sharing">
        <p>We do not sell your personal information. Information may be shared with financial institutions only in the context of your financing requirement and with your consent.</p>
      </Section>
      <Section title="Data Security & Retention" testId="privacy-security">
        <p>We apply reasonable technical and organizational measures to protect your data. Enquiry records are retained only as long as necessary to serve your requirement and meet compliance obligations.</p>
      </Section>
      <Section title="Your Choices" testId="privacy-choices">
        <p>You may request access, correction, or deletion of your personal information by writing to us at connect@lendxpro.com.</p>
      </Section>
    </LegalLayout>
  );
}

export function TermsPage() {
  return (
    <LegalLayout
      overline="Legal"
      title="Terms & Conditions"
      sub="The terms governing the use of the LendX website and services."
      testId="terms"
      seoTitle="Terms & Conditions | LendX"
    >
      <Section title="Nature of Services" testId="terms-nature">
        <p>LendX is a financial advisory and facilitation platform. We are not a bank, NBFC, or direct lender. All financing is provided by independent financial institutions under their own terms.</p>
      </Section>
      <Section title="No Guarantee of Approval" testId="terms-approval">
        <p>Submitting an enquiry does not guarantee loan approval, any specific interest rate, or disbursement. All approvals are subject to the policies, eligibility criteria, and final decision of the respective financial institution.</p>
      </Section>
      <Section title="Accuracy of Information" testId="terms-accuracy">
        <p>You agree to provide accurate and complete information in all enquiries. LendX is not responsible for outcomes arising from inaccurate or incomplete information provided by you.</p>
      </Section>
      <Section title="Intellectual Property" testId="terms-ip">
        <p>All content on this website, including text, design, and branding, is the property of LendX and may not be reproduced without written permission.</p>
      </Section>
      <Section title="Limitation of Liability" testId="terms-liability">
        <p>LendX provides advisory and facilitation services on a best-effort basis and shall not be liable for decisions made by financial institutions or for indirect losses arising from the use of this website.</p>
      </Section>
    </LegalLayout>
  );
}

export function DisclaimerPage() {
  return (
    <LegalLayout
      overline="Legal"
      title="Disclaimer"
      sub="Important information about the nature of LendX services."
      testId="disclaimer"
      seoTitle="Disclaimer | LendX"
    >
      <Section title="Advisory & Facilitation Only" testId="disclaimer-nature">
        <p className="rounded-lg border border-gold-line bg-gold-tint p-5 text-gray-800">{DISCLAIMER}</p>
      </Section>
      <Section title="No Assured Outcomes" testId="disclaimer-outcomes">
        <p>Nothing on this website constitutes a guarantee of loan approval, assured interest rates, or committed disbursement timelines. Illustrations and descriptions are indicative and informational only.</p>
      </Section>
      <Section title="Independent Decision" testId="disclaimer-decision">
        <p>Visitors should evaluate all financing terms independently and seek professional advice where needed before entering into any financial commitment.</p>
      </Section>
    </LegalLayout>
  );
}
